/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './lib/firebase';
import { dbService } from './services/dbService';
import { AnimatePresence, motion } from 'motion/react';
import { Header, Footer } from './components/Navigation';

// Page Imports
import LandingPage from './pages/LandingPage';
import AuditHub from './pages/AuditHub';
import AuthPage from './pages/AuthPage';
import AttachmentROIAudit from './pages/AttachmentROIAudit';
import ExecutiveAttachmentAudit from './pages/ExecutiveAttachmentAudit';
import WorthinessGapAudit from './pages/WorthinessGapAudit';
import IntimidationDelusionAudit from './pages/IntimidationDelusionAudit';
import FairPlayCPEAudit from './pages/FairPlayCPEAudit';
import MemberDashboard from './pages/MemberDashboard';
import AffiliateCuration from './pages/AffiliateCuration';
import BlogLibrary from './pages/BlogLibrary';
import ResourceVault from './pages/ResourceVault';
import PhilosophyPage from './pages/PhilosophyPage';
import DossierDetail from './pages/DossierDetail';
import RecalibrationWorksheet from './pages/RecalibrationWorksheet';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AnimatedRoutes({ user, loading }: { user: User | null, loading: boolean }) {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="w-12 h-12 border-t-2 border-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <div key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <LandingPage />
            </motion.div>
          } />
          <Route path="/auth" element={
            user && user.emailVerified ? <Navigate to="/dashboard" /> : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <AuthPage />
              </motion.div>
            )
          } />
          <Route path="/audit" element={
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <AuditHub />
            </motion.div>
          } />
          <Route path="/audit/attachment-roi" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <AttachmentROIAudit />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/audit/executive-attachment" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <ExecutiveAttachmentAudit />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/audit/worthiness-gap" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <WorthinessGapAudit />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/audit/intimidation-delusion" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <IntimidationDelusionAudit />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/audit/fair-play-cpe" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <FairPlayCPEAudit />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/dashboard" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <MemberDashboard />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/dashboard/dossier/:id" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <DossierDetail />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/curation" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <AffiliateCuration />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/library" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <BlogLibrary />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/vault" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <ResourceVault />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/recalibration" element={
            user && user.emailVerified ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <RecalibrationWorksheet />
              </motion.div>
            ) : <Navigate to="/auth" />
          } />
          <Route path="/philosophy" element={
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <PhilosophyPage />
            </motion.div>
          } />
        </Routes>
      </div>
    </AnimatePresence>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser && currentUser.emailVerified) {
        // Silently ensure profile exists on every refresh/login
        try {
          await dbService.ensureUserProfile(currentUser.uid, currentUser.email, currentUser.displayName);
        } catch (err) {
          console.error("Profile sync error on refresh:", err);
        }
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen selection:bg-gold selection:text-charcoal cursor-default">
        <Header />
        <main className="flex-grow">
          <AnimatedRoutes user={user} loading={loading} />
        </main>
        <Footer />
      </div>
    </Router>
  );
}

